import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import SEOMeta from '@/components/base/SEOMeta';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

const categoryLabelMap: Record<string, string> = {
  'platform-intro': '平台介绍',
  'usage-guide': '使用教程',
  'account-security': '账号安全',
  'latest-news': '最新动态',
  'faq-help': '常见问题',
  'community': '社区交流',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <SEOMeta
          title="文章未找到 - 91com官方平台"
          description="抱歉，您查找的文章不存在或已被移除。"
          keywords="91com,91com官网"
          canonicalUrl={siteInfo.url}
        />
        <Navbar />
        <main className="pt-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = categoryLabelMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - 91com官方平台`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteInfo.url}/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
      />
      <SchemaOrg
        pageType="article"
        articleTitle={article.title}
        articleDate={article.date}
        articleAuthor={article.author}
        articleImage={article.imageUrl}
      />

      <Navbar />

      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: categoryLabel, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {categoryLabel}
              </Link>
              <span className="text-xs text-foreground-500">{article.date}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 阅读
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-content text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-8 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-600">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share */}
          <div className="mt-6 flex items-center gap-3">
            <span className="text-sm text-foreground-600">分享到：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
              <i className="ri-wechat-line text-foreground-700"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
              <i className="ri-weibo-line text-foreground-700"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200 cursor-pointer" aria-label="复制链接">
              <i className="ri-link text-foreground-700"></i>
            </button>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="block bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-all duration-300 group cursor-pointer"
                >
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {ra.title}
                  </h3>
                  <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{ra.excerpt}</p>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span>{ra.date}</span>
                    <span>{ra.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Back Navigation */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-14">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-background-200/70">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{categoryLabel}
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}